/**
 * Tag Combat Effects
 * Single source of truth for tag → combat effect mappings.
 * Used by both players (via resolved tag slugs) and NPCs (via scenario tags).
 */

import type { MagicDiscipline, Stats } from './types';

// ============================================================================
// TYPES
// ============================================================================

export interface TagCombatEffect {
  defense?: number;
  skills?: string[];
  stats?: Partial<Stats>;
  magicDiscipline?: MagicDiscipline;
  speed?: number;
}

// ============================================================================
// CATALOG
// ============================================================================

export const TAG_COMBAT_EFFECTS: Record<string, TagCombatEffect> = {
  // --- Defense ---
  armored:         { defense: 1 },
  heavily_armored: { defense: 2 },
  plate:           { defense: 2 },

  // --- Brawn / strength ---
  brute:   { skills: ['shield_bash'], stats: { brawn: 4, grace: 1 }, speed: 3 },
  strong:  { stats: { brawn: 4, grace: 1 }, speed: 3 },
  heavy:   { stats: { brawn: 4, grace: 1 }, speed: 3 },

  // --- Grace / agility ---
  agile:   { stats: { grace: 4 }, speed: 5 },
  quick:   { stats: { grace: 4 }, speed: 5 },
  nimble:  { stats: { grace: 4 }, speed: 5 },

  // --- Wits / cunning ---
  cunning:  { skills: ['feint', 'nimble_strike'], stats: { wits: 4 } },
  smart:    { stats: { wits: 4 } },
  tactical: { skills: ['feint'], stats: { wits: 4 } },

  // --- Spirit / magic ---
  magical: { stats: { spirit: 4 }, magicDiscipline: 'ember' },
  priest:  { stats: { spirit: 4 }, magicDiscipline: 'ember' },
  shaman:  { stats: { spirit: 4 }, magicDiscipline: 'ember' },

  // --- Skill-granting roles ---
  berserker: { skills: ['whirlwind'] },
  guardian:  { skills: ['shield_wall', 'taunt'] },
  healer:    { skills: ['second_wind', 'rallying_shout'], magicDiscipline: 'vitality' },
  commander: { skills: ['battle_cry', 'rallying_shout'] },
  assassin:  { skills: ['nimble_strike', 'feint'] },
  archer:    { skills: ['disengage'] },
  protector: { skills: ['shield_wall', 'taunt'] },
  savage:    { skills: ['whirlwind'] },
  leader:    { skills: ['battle_cry'] },

  // --- Magic discipline (specific) ---
  kinetic:     { magicDiscipline: 'kinetic' },
  ember:       { magicDiscipline: 'ember' },
  fire:        { magicDiscipline: 'ember' },
  vitality:    { magicDiscipline: 'vitality' },
  veil:        { magicDiscipline: 'veil' },
  shadow:      { magicDiscipline: 'veil' },
  influence:   { magicDiscipline: 'influence' },
  dominate:    { magicDiscipline: 'influence' },
  spellcaster: { magicDiscipline: 'ember' },
};

// ============================================================================
// RESOLUTION FUNCTIONS
// ============================================================================

/**
 * Resolve passive defense from tags. Uses max aggregation —
 * wearing plate (2) + armored (1) yields 2, not 3.
 */
export function resolveDefense(tags: string[]): number {
  let max = 0;
  for (const tag of tags) {
    const effect = TAG_COMBAT_EFFECTS[tag.toLowerCase()];
    if (effect?.defense !== undefined && effect.defense > max) {
      max = effect.defense;
    }
  }
  return max;
}

/**
 * Resolve skill IDs from tags. Union aggregation — all skills
 * from all matching tags, deduplicated.
 */
export function resolveSkills(tags: string[]): string[] {
  const skillSet = new Set<string>();
  for (const tag of tags) {
    const effect = TAG_COMBAT_EFFECTS[tag.toLowerCase()];
    if (effect?.skills) {
      for (const s of effect.skills) skillSet.add(s);
    }
  }
  return Array.from(skillSet);
}

/**
 * Resolve NPC stats from tags.
 * Base: {2, 2, 2, 2}. Tag stats override individual values (SET semantics).
 * Post-processing: weak/minion → all stats 1; boss/elite/leader → floor 3 on brawn/grace/wits.
 */
export function resolveNpcStats(tags: string[]): Stats {
  const lowercaseTags = tags.map(t => t.toLowerCase());

  let brawn = 2, grace = 2, wits = 2, spirit = 2;

  for (const tag of lowercaseTags) {
    const effect = TAG_COMBAT_EFFECTS[tag];
    if (effect?.stats) {
      if (effect.stats.brawn !== undefined) brawn = effect.stats.brawn;
      if (effect.stats.grace !== undefined) grace = effect.stats.grace;
      if (effect.stats.wits !== undefined) wits = effect.stats.wits;
      if (effect.stats.spirit !== undefined) spirit = effect.stats.spirit;
    }
  }

  // Weak/minion override: all stats → 1
  if (lowercaseTags.some(t => t === 'weak' || t === 'minion')) {
    brawn = 1; grace = 1; wits = 1; spirit = 1;
  }

  // Boss/elite/leader: floor brawn/grace/wits to 3
  if (lowercaseTags.some(t => t === 'boss' || t === 'elite' || t === 'leader')) {
    brawn = Math.max(brawn, 3);
    grace = Math.max(grace, 3);
    wits = Math.max(wits, 3);
  }

  return { brawn, grace, wits, spirit };
}

// Discipline priority: specific disciplines first, generic casters last
const DISCIPLINE_PRIORITY: Array<{ tags: string[]; discipline: Exclude<MagicDiscipline, 'none'> }> = [
  { tags: ['kinetic'],                           discipline: 'kinetic' },
  { tags: ['ember', 'fire'],                     discipline: 'ember' },
  { tags: ['vitality', 'healer'],                 discipline: 'vitality' },
  { tags: ['veil', 'shadow'],                    discipline: 'veil' },
  { tags: ['influence', 'dominate'],             discipline: 'influence' },
  { tags: ['magical', 'priest', 'shaman', 'spellcaster'], discipline: 'ember' },
];

/**
 * Resolve magic discipline from tags. First match wins
 * against a fixed priority list (specific → generic).
 */
export function resolveMagicDiscipline(tags: string[]): MagicDiscipline {
  const lowercaseTags = new Set(tags.map(t => t.toLowerCase()));

  for (const entry of DISCIPLINE_PRIORITY) {
    if (entry.tags.some(t => lowercaseTags.has(t))) {
      return entry.discipline;
    }
  }

  return 'none';
}

/**
 * Resolve movement speed from tags and stats.
 * Tags use max aggregation (same pattern as resolveDefense).
 * Fallback formula: 3 + floor(grace / 2).
 * Post-processing: weak/minion → 3, boss/elite → 4.
 */
export function resolveSpeed(tags: string[], stats: Stats): number {
  const lowercaseTags = tags.map(t => t.toLowerCase());

  // Check tag overrides (max aggregation)
  let tagSpeed = 0;
  for (const tag of lowercaseTags) {
    const effect = TAG_COMBAT_EFFECTS[tag];
    if (effect?.speed !== undefined && effect.speed > tagSpeed) {
      tagSpeed = effect.speed;
    }
  }

  // Base speed from grace if no tag override
  let speed = tagSpeed > 0 ? tagSpeed : 3 + Math.floor(stats.grace / 2);

  // Weak/minion override
  if (lowercaseTags.some(t => t === 'weak' || t === 'minion')) {
    speed = 3;
  }

  // Boss/elite floor
  if (lowercaseTags.some(t => t === 'boss' || t === 'elite')) {
    speed = Math.max(speed, 4);
  }

  return speed;
}
